import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { LocationService, ImportLocationResult } from '../../services/locationService';
import { parseLocationImportData } from '../../utils/locationCsvTemplateGenerator';
import csvService from '../../services/csvService';
import './LocationImportModal.css';

interface LocationImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  organizationId?: number;
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'complete' | 'error';

const LocationImportModal: React.FC<LocationImportModalProps> = ({
  isOpen,
  onClose,
  onImportComplete,
  organizationId
}) => {
  const { theme } = useTheme();
  const [currentStep, setCurrentStep] = useState<ImportStep>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [importResult, setImportResult] = useState<ImportLocationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const resetModal = () => {
    setCurrentStep('upload');
    setSelectedFile(null);
    setCsvData([]);
    setImportResult(null);
    setError(null);
    setIsProcessing(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setSelectedFile(file);
    setError(null);
    setIsProcessing(true);

    try {
      // Parse CSV file for preview
      const csvFile = await csvService.parseCSV(file);
      console.log('📊 Parsed CSV for preview:', { columns: csvFile.columns, rowCount: csvFile.rows.length });
      
      // Construct the data array for preview
      const csvData = [csvFile.columns, ...csvFile.rows];
      setCsvData(csvData);
      setCurrentStep('preview');
    } catch (err) {
      console.error('❌ Error parsing CSV:', err);
      setError(err instanceof Error ? err.message : 'Failed to parse CSV file');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setCurrentStep('importing');
    setError(null);

    try {
      const result = await LocationService.importLocations(selectedFile, organizationId);
      setImportResult(result);
      
      if (result.success) {
        setCurrentStep('complete');
        onImportComplete();
      } else {
        setCurrentStep('error');
      }
    } catch (err) {
      console.error('❌ Import failed:', err);
      setError(err instanceof Error ? err.message : 'Import failed');
      setCurrentStep('error');
    }
  };

  const handleStartOver = () => {
    resetModal();
  };

  if (!isOpen) return null;

  const previewRows = csvData.slice(0, 21); // Show header + 20 data rows
  const hasMoreRows = csvData.length > 21;

  return (
    <div className="modal-overlay">
      <div className={`location-import-modal theme-${theme}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>Import Locations from CSV</h2>
            <button className="close-button" onClick={handleClose}>×</button>
          </div>

          {currentStep === 'upload' && (
            <div className="import-step">
              <div className="step-header">
                <h3>📁 Step 1: Upload CSV File</h3>
                <p>Select a CSV file containing location data to import</p>
              </div>

              <div className="upload-area">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  disabled={isProcessing}
                  id="csv-file-input"
                  className="file-input"
                />
                <label htmlFor="csv-file-input" className="file-input-label">
                  <div className="upload-icon">📄</div>
                  <div className="upload-text">
                    {isProcessing ? 'Processing...' : 'Choose CSV File'}
                  </div>
                  <div className="upload-subtext">
                    Click to browse or drag and drop
                  </div>
                </label>
              </div>

              <div className="upload-requirements">
                <h4>📋 CSV Requirements:</h4>
                <ul>
                  <li><strong>name</strong> - Location name (required)</li>
                  <li><strong>code</strong> - Location code (optional)</li>
                  <li><strong>address</strong> - Full address (optional)</li>
                </ul>
                <p className="note">
                  💡 <strong>Tip:</strong> Download the template to see the correct format
                </p>
              </div>

              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}
            </div>
          )}

          {currentStep === 'preview' && (
            <div className="import-step">
              <div className="step-header">
                <h3>👀 Step 2: Preview Data</h3>
                <p>Review the locations that will be imported ({csvData.length - 1} locations found)</p>
              </div>

              <div className="preview-table-container">
                <table className="preview-table">
                  <thead>
                    <tr>
                      {previewRows[0]?.map((header, index) => (
                        <th key={index}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.slice(1).map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td 
                            key={cellIndex}
                            className={previewRows[0]?.[cellIndex]?.toLowerCase().includes('address') ? 'address' : ''}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {hasMoreRows && (
                <p className="preview-note">
                  ... and {csvData.length - 21} more locations
                </p>
              )}

              <div className="step-actions">
                <button className="btn-secondary" onClick={handleStartOver}>
                  Choose Different File
                </button>
                <button className="btn-primary" onClick={handleImport}>
                  Import {csvData.length - 1} Locations
                </button>
              </div>
            </div>
          )}

          {currentStep === 'importing' && (
            <div className="import-step">
              <div className="step-header">
                <h3>⏳ Importing Locations...</h3>
                <p>Please wait while we process your locations</p>
              </div>

              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Processing {csvData.length - 1} locations...</p>
              </div>
            </div>
          )}

          {currentStep === 'complete' && importResult && (
            <div className="import-step">
              <div className="step-header success">
                <h3>✅ Import Complete!</h3>
                <p>{importResult.message}</p>
              </div>

              <div className="import-summary">
                <div className="summary-item success">
                  <span className="summary-label">Imported:</span>
                  <span className="summary-value">{importResult.data?.imported || 0} locations</span>
                </div>
                
                {importResult.data?.warnings && importResult.data.warnings.length > 0 && (
                  <div className="summary-item warning">
                    <span className="summary-label">Warnings:</span>
                    <span className="summary-value">{importResult.data.warnings.length}</span>
                  </div>
                )}
                
                {importResult.data?.errors && importResult.data.errors.length > 0 && (
                  <div className="summary-item error">
                    <span className="summary-label">Errors:</span>
                    <span className="summary-value">{importResult.data.errors.length}</span>
                  </div>
                )}
              </div>

              {importResult.data?.warnings && importResult.data.warnings.length > 0 && (
                <div className="import-details">
                  <h4>⚠️ Warnings:</h4>
                  <ul className="detail-list warning">
                    {importResult.data.warnings.map((warning, index) => (
                      <li key={index}>
                        Row {warning.row}: {warning.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {importResult.data?.errors && importResult.data.errors.length > 0 && (
                <div className="import-details">
                  <h4>❌ Errors:</h4>
                  <ul className="detail-list error">
                    {importResult.data.errors.map((error, index) => (
                      <li key={index}>
                        Row {error.row}: {error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="step-actions">
                <button className="btn-primary" onClick={handleClose}>
                  Close
                </button>
                <button className="btn-secondary" onClick={handleStartOver}>
                  Import More Locations
                </button>
              </div>
            </div>
          )}

          {currentStep === 'error' && (
            <div className="import-step">
              <div className="step-header error">
                <h3>❌ Import Failed</h3>
                <p>There was an error importing your locations</p>
              </div>

              <div className="error-details">
                <p><strong>Error:</strong> {error || importResult?.message}</p>
                
                {importResult?.data?.errors && importResult.data.errors.length > 0 && (
                  <div className="import-details">
                    <h4>Details:</h4>
                    <ul className="detail-list error">
                      {importResult.data.errors.map((error, index) => (
                        <li key={index}>
                          Row {error.row}: {error.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="step-actions">
                <button className="btn-secondary" onClick={handleClose}>
                  Close
                </button>
                <button className="btn-primary" onClick={handleStartOver}>
                  Try Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationImportModal;
















